import React from 'react'
import { Button } from 'primereact/button'
import { PersonApi, PersonUpdateDto } from '../../../api'
import { InputText } from 'primereact/inputtext'
import { Calendar } from 'primereact/calendar'
import { Loading } from '../../loading/Loading'
import { authState } from '../../../state/Auth.state'
import { appState } from '../../../state/App.state'
import { useLocalState } from '../../../shared/UseLocalState'

const buildForm = (): PersonUpdateDto => {
    const user = authState.user$.value
    if (!user) return {}
    const birthday = user.birthday ? new Date(user.birthday) : null
    if (birthday) birthday.setMinutes(birthday.getMinutes() - birthday.getTimezoneOffset())
    return {
        firstName:       user.firstName      ?? '',
        lastName:        user.lastName        ?? '',
        email:           user.email           ?? '',
        phoneNumber:     user.phoneNumber     ?? '',
        street:          user.street          ?? '',
        zipCode:         user.zipCode         ?? '',
        city:            user.city            ?? '',
        country:         user.country         ?? '',
        birthday,
        bankAccountIBAN: user.bankAccountIBAN ?? '',
        bankOwner:       user.bankOwner?.trim() ? user.bankOwner : ((user.firstName ?? '') + ' ' + (user.lastName ?? '')).trim(),
        password:        '',
    }
}

const validateIban = (value: string): boolean => {
    const raw = value.replace(/\s/g, '').toUpperCase()
    if (raw.length === 0) return true
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(raw)) return false
    const rearranged = raw.slice(4) + raw.slice(0, 4)
    const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55))
    let remainder = 0
    for (const chunk of numeric.match(/.{1,9}/g) ?? []) {
        remainder = Number(String(remainder) + chunk) % 97
    }
    return remainder === 1
}

export const AddressChange = () => {
    /* STATE */
    const user = useLocalState(authState.user$, authState.user$.value)
    const igvNumber = useLocalState(authState.igvNumber$, authState.igvNumber$.value)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [form, setForm] = React.useState<PersonUpdateDto>(buildForm)
    const initialForm = React.useRef<PersonUpdateDto>(buildForm())

    /* DERIVED STATE */
    const ibanValid = React.useMemo(() => validateIban(form.bankAccountIBAN ?? ''), [form.bankAccountIBAN])
    const ibanDirty = (form.bankAccountIBAN ?? '').replace(/\s/g, '').length > 0

    const requiredFieldsValid = React.useMemo(() =>
        (form.firstName?.trim().length ?? 0) > 0
        && (form.lastName?.trim().length  ?? 0) > 0
        && (form.email?.trim().length     ?? 0) > 3
        && (form.email?.includes('@')     ?? false)
    , [form])

    const hasChanges = React.useMemo(() => {
        const i = initialForm.current
        return form.firstName       !== i.firstName
            || form.lastName        !== i.lastName
            || form.email           !== i.email
            || form.phoneNumber     !== i.phoneNumber
            || form.street          !== i.street
            || form.zipCode         !== i.zipCode
            || form.city            !== i.city
            || form.country         !== i.country
            || form.bankAccountIBAN !== i.bankAccountIBAN
            || form.bankOwner       !== i.bankOwner
            || (form.birthday?.getTime() ?? 0) !== (i.birthday?.getTime() ?? 0)
            || (form.password?.length    ?? 0)  > 0
    }, [form])

    const saveEnabled = igvNumber != null && requiredFieldsValid && hasChanges && ibanValid

    /* LOGIC */
    const setField = React.useCallback(<K extends keyof PersonUpdateDto>(key: K, val: PersonUpdateDto[K]) => {
        setForm((prev: PersonUpdateDto) => ({ ...prev, [key]: val }))
    }, [])

    const handleCancel = React.useCallback(() => {
        window.history.back()
    }, [])

    const handleSubmit = React.useCallback(() => {
        if (igvNumber == null) return
        setLoading(true)
        new PersonApi(authState.apicfg())
            .apiPersonIgvKennungPut({ igvKennung: igvNumber, personUpdateDto: form })
            .then(() => {
                appState.addToast('Daten gespeichert. Bitte neu anmelden.', 'success')
                authState.logout()
            })
            .catch((err: unknown) => {
                appState.addToast('Speichern fehlgeschlagen!', 'error')
                console.error(err)
            })
            .finally(() => setLoading(false))
    }, [igvNumber, form])

    /* TEMPLATE */
    return (
        <>
            <Loading loading={loading} />
            <div className='card' style={{ maxWidth: '600px', margin: '1rem auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <h2 style={{ marginBottom: '0.25rem' }}>Adressänderung</h2>
                <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>
                    Bitte prüfen und aktualisieren Sie Ihre Daten.
                    Speichern wird aktiv sobald Sie etwas geändert haben.
                </p>

                {/* Name */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <InputText style={{ flex: 1 }} placeholder='Vorname *' value={form.firstName ?? ''} onChange={(e) => setField('firstName', e.target.value)} />
                    <InputText style={{ flex: 1 }} placeholder='Nachname *' value={form.lastName ?? ''} onChange={(e) => setField('lastName', e.target.value)} />
                </div>

                {/* Birthday */}
                <Calendar
                    placeholder='Geburtsdatum'
                    value={form.birthday ?? null}
                    dateFormat='dd.mm.yy'
                    onChange={(e) => {
                        const d = e.value as Date ?? null
                        if (d) d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
                        setField('birthday', d)
                    }}
                    showIcon
                />

                {/* Contact */}
                <InputText type='email' placeholder='E-Mail *' value={form.email ?? ''} onChange={(e) => setField('email', e.target.value)} />
                <InputText placeholder='Telefon / Mobil' value={form.phoneNumber ?? ''} onChange={(e) => setField('phoneNumber', e.target.value)} />

                {/* Address */}
                <InputText placeholder='Straße' value={form.street ?? ''} onChange={(e) => setField('street', e.target.value)} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <InputText style={{ flex: '0 0 6rem' }} placeholder='PLZ' value={form.zipCode ?? ''} onChange={(e) => setField('zipCode', e.target.value)} />
                    <InputText style={{ flex: 1 }} placeholder='Ort' value={form.city ?? ''} onChange={(e) => setField('city', e.target.value)} />
                    <InputText style={{ flex: '0 0 6rem' }} placeholder='Land' value={form.country ?? ''} onChange={(e) => setField('country', e.target.value)} />
                </div>

                {/* Bank account */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <InputText
                        placeholder='IBAN'
                        value={form.bankAccountIBAN ?? ''}
                        onChange={(e) => setField('bankAccountIBAN', e.target.value)}
                        className={ibanDirty && !ibanValid ? 'p-invalid' : ''}
                    />
                    {ibanDirty && !ibanValid && (
                        <small style={{ color: 'var(--red-500)' }}>Ungültige IBAN</small>
                    )}
                </div>
                <InputText placeholder='Kontoinhaber' value={form.bankOwner ?? ''} onChange={(e) => setField('bankOwner', e.target.value)} />

                {/* New password */}
                <InputText type='password' placeholder='Neues Passwort (leer lassen = unverändert)' value={form.password ?? ''} onChange={(e) => setField('password', e.target.value)} />

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button disabled={!saveEnabled} onClick={handleSubmit} style={{ flex: 1 }}>
                        Speichern
                    </Button>
                    <Button outlined onClick={handleCancel} style={{ flex: 1 }}>
                        Abbrechen
                    </Button>
                </div>
            </div>
        </>
    )
}
